import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, Alert, Chip, Divider, CircularProgress } from '@mui/material';
import { Warning, Delete, People } from '@mui/icons-material';
import type { TenantWithStatus } from '../types';

export interface TenantDeleteConfirmationProps {
    open: boolean;
    tenant: TenantWithStatus | null;
    onConfirm: (tenantId: string) => Promise<void>;
    onCancel: () => void;
    loading: boolean;
}

export function TenantDeleteConfirmation({ open, tenant, onConfirm, onCancel, loading }: TenantDeleteConfirmationProps) {
    const [confirmationText, setConfirmationText] = useState('');
    const [error, setError] = useState<string>('');

    if (!tenant) {
        return null;
    }

    const hasActiveUsers = tenant.userCount && tenant.userCount > 0;
    const isConfirmationValid = confirmationText === tenant.name;

    const handleConfirm = async () => {
        if (!isConfirmationValid) {
            setError('Please type the tenant name exactly as shown to confirm deletion');
            return;
        }

        try {
            setError('');
            await onConfirm(tenant.tenantId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete tenant');
        }
    };

    const handleCancel = () => {
        setConfirmationText('');
        setError('');
        onCancel();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: { minHeight: '400px' },
                },
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <Warning />
                    <Typography variant="h5" component="h2" color="error.main">
                        Delete Tenant
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This action cannot be undone. All tenant data will be permanently deleted.
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Error Alert */}
                    {error && <Alert severity="error">{error}</Alert>}

                    {/* Active Users Warning */}
                    {hasActiveUsers && (
                        <Alert
                            severity="warning"
                            icon={<People />}
                            sx={{
                                '& .MuiAlert-message': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                },
                            }}
                        >
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Warning: Tenant has active users
                                </Typography>
                                <Typography variant="body2">
                                    This tenant has {tenant.userCount} active user{tenant.userCount !== 1 ? 's' : ''}. Deleting this tenant will also remove all
                                    associated user accounts and their data.
                                </Typography>
                            </Box>
                        </Alert>
                    )}

                    {/* Tenant Details */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Tenant Details
                        </Typography>

                        <Box
                            sx={{
                                p: 2,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: 'grey.50',
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Name:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {tenant.name}
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Subdomain:
                                    </Typography>
                                    <Typography variant="body1" fontFamily="monospace">
                                        {tenant.subDomain}
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Region:
                                    </Typography>
                                    <Typography variant="body1">{tenant.region}</Typography>
                                </Box>

                                <Divider />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Status:
                                    </Typography>
                                    <Chip
                                        label={tenant.status || 'unknown'}
                                        size="small"
                                        color={
                                            tenant.status === 'active'
                                                ? 'success'
                                                : tenant.status === 'error'
                                                  ? 'error'
                                                  : tenant.status === 'pending' || tenant.status === 'provisioning'
                                                    ? 'warning'
                                                    : 'default'
                                        }
                                    />
                                </Box>

                                {tenant.userCount !== undefined && (
                                    <>
                                        <Divider />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Active Users:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium" color={tenant.userCount > 0 ? 'warning.main' : 'text.primary'}>
                                                {tenant.userCount}
                                            </Typography>
                                        </Box>
                                    </>
                                )}

                                <Divider />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Created:
                                    </Typography>
                                    <Typography variant="body1">{new Date(tenant.createdAt).toLocaleDateString()}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Confirmation Input */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Confirmation Required
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            To confirm deletion, please type the tenant name <strong>{tenant.name}</strong> in the field below:
                        </Typography>

                        <TextField
                            label="Type tenant name to confirm"
                            value={confirmationText}
                            onChange={(e) => setConfirmationText(e.target.value)}
                            fullWidth
                            required
                            disabled={loading}
                            error={confirmationText.length > 0 && !isConfirmationValid}
                            helperText={
                                confirmationText.length > 0 && !isConfirmationValid ? 'Tenant name does not match' : `Type "${tenant.name}" to enable deletion`
                            }
                            placeholder={tenant.name}
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button onClick={handleCancel} disabled={loading} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={loading || !isConfirmationValid}
                    variant="contained"
                    color="error"
                    startIcon={loading ? <CircularProgress size={20} /> : <Delete />}
                >
                    {loading ? 'Deleting...' : 'Delete Tenant'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
